// ── 团队成员人才背景信号检测 ──────────────────────────────────
// 输入是团队成员公开资料里的 bio/company 文本（数据来源见调用方：
// teamEnhanced.js 的 fetchTopContributors() 走 GitHub 公开 API，earlyBirdWiki.js
// 同样复用该函数）。纯文本关键词匹配，不发起任何网络请求，不做 AI 推断——
// 只认当事人自己写在公开资料里的原文，不允许编造或推断。
//
// 两类信号：
// - elite_institution：曾任职于顶尖科技/量化机构，多为 AI 前沿/大厂/传统金融背景迁移信号
// - repeat_founder：明确提及此前创立/联合创立过其他公司或项目，连续创业者信号

const ELITE_INSTITUTION_KEYWORDS = [
  'google', 'deepmind', 'openai', 'anthropic', 'meta', 'facebook', 'microsoft',
  'amazon', 'apple', 'coinbase', 'stripe', 'jump trading', 'jump crypto',
  'citadel', 'jane street', 'susquehanna', 'two sigma', 'd. e. shaw',
  'goldman sachs', 'blackrock', 'palantir', 'netflix', 'uber', 'mckinsey',
  'scale ai', 'lockheed',
  // 常见的公司缩写/股票代码写法（GitHub bio 常见），只收录足够长、不易与普通词
  // 撞车的几个——"cb"(Coinbase) 这类两字符缩写太容易误命中，不收录
  'msft', 'amzn', 'twtr',
];

const SERIAL_FOUNDER_PHRASES = [
  'prev:', 'previously', 'formerly', 'founder of', 'co-founder of', 'ex-founder',
];

// GitHub 上常见的 AI 助手 / 自动化账号——commit co-author 挂名会让这些账号出现在
// contributors 列表里，它们的 bio/company 完全可能命中上面的关键词（比如 @anthropics
// 官方账号"claude"就会被误判成"顶尖机构履历"），但这不是任何人的履历，得在算
// pedigreeSignal 之前就排除掉，不能靠 detectPedigreeSignal 本身判断（它只看文本）。
const KNOWN_NON_HUMAN_LOGINS = new Set([
  'claude', 'claude-code', 'chatgpt', 'openai-codex', 'copilot', 'copilot-swe-agent',
  'devin-ai-integration', 'devin-ai-integration[bot]', 'dependabot', 'dependabot[bot]',
  'renovate', 'renovate[bot]', 'github-actions', 'github-actions[bot]',
  'greenkeeper', 'greenkeeperio-bot', 'imgbot', 'allcontributors', 'allcontributors[bot]',
  'semantic-release-bot', 'snyk-bot',
]);

/**
 * @param {string|null|undefined} login - GitHub 用户名
 * @param {string|null|undefined} [accountType] - GitHub /users/{login} 返回的 type 字段
 * @returns {boolean}
 */
export function isLikelyNonHumanContributor(login, accountType) {
  if (accountType === 'Bot') return true;
  const l = (login || '').toLowerCase();
  if (l.endsWith('[bot]')) return true;
  return KNOWN_NON_HUMAN_LOGINS.has(l);
}

/**
 * @param {string|null|undefined} bio
 * @param {string|null|undefined} company
 * @returns {{ type: 'elite_institution'|'repeat_founder'|'both', evidence: string } | null}
 */
export function detectPedigreeSignal(bio, company) {
  const bioText = (bio || '').toLowerCase();
  const companyText = (company || '').toLowerCase();
  const combined = `${bioText} ${companyText}`;
  if (!combined.trim()) return null;

  const matches = (t) => ELITE_INSTITUTION_KEYWORDS.some((k) => t.includes(k)) || SERIAL_FOUNDER_PHRASES.some((k) => t.includes(k));
  const hasElite = ELITE_INSTITUTION_KEYWORDS.some((k) => combined.includes(k));
  const hasSerial = SERIAL_FOUNDER_PHRASES.some((k) => combined.includes(k));
  if (!hasElite && !hasSerial) return null;

  // evidence 取真正命中关键词的那个字段，不是无脑先 bio 后 company——两个字段都非空时
  // （比如 LinkedIn bio + priorWork 场景），bio 本身可能完全没有信号，命中的是
  // company/priorWork 那一侧，证据文本也应该来自那边，不然会展示一句不相关的话。
  const evidenceText = matches(bioText) ? bio : (company || bio);

  return {
    type: hasElite && hasSerial ? 'both' : hasElite ? 'elite_institution' : 'repeat_founder',
    evidence: (evidenceText || '').slice(0, 200),
  };
}
